import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Card, Dropdown, List} from 'tea-component';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import {useMemoizedFn, useRequest} from 'ahooks';
import {useIntl, useParams} from 'umi';
import { calculateDashboardChart, previewDashboardChartSql } from '@dbl/services/dashboard';
import type { IDashboardChartRequest } from '@dbl/services/dashboard';
import {getTrendDateInfo, hasEmptyProperty} from '@/pages/datalab/utils/utils';
import Abnormal from '@home/components/Abnormal';
import {SYSTEM_NAME} from '@dbl/core/global';
// import ForceBubble from "@/pages/datalab/components/ForceBubble";
import {
  getScopeFilters,
} from "@/pages/datalab/components/DashboardLayout/components/DragShell/_utils";
import DimNumberTrendCard from "@customComp/DimNumberTrendCard";
import { ChartType } from '@dbl/core/chart';
import './index.scss';
import {isEmpty} from 'lodash-es';
import {handleExportClick} from "@/pages/datalab/components/DashboardLayout/_utils";
import {formatDisplayValue} from "@customComp/Chart/_config/_utils";
import { getOldChartDisplayConfig } from '@dbl/core/chartEditor';
import ShowFilterContentIcon, {
  FILTER_CONTENT_KEY
} from "@/pages/datalab/components/ChartCardIconButtons/ShowFilterContentIcon";
import {useChartCardError} from '@dbl/components/DashboardLayout/components/DragShell/hooks/useChartCardError';
import {useClosePopoversWhenAnchorHidden} from './useClosePopoversWhenAnchorHidden';
import {getCardOverlayProps} from './getCardOverlayProps';
import { usePreviewSQL, PreviewSQLModal } from '@dbl/domainComponents/dataSource';

const DimNumberTrendTragCard = (props: any) => {
  const {
    deleteCard,
    handleOnSubmit,
    dataMsg,
    filterValues,
    userFilter,
    isEdit = true,
    hasChartPrivilege,
    system = 'datalab',
    chartExplore,
    // onForceClose,
    chartsInfoLoading,
    isFilterInit, // 是否为 页面初始进入状态(没有点击confirm, 或者点击了reset)
  } = props;
  const {task_id: taskId} = dataMsg || {};
  const [open, setOpen] = useState(false);
  const cardRootRef = useRef<HTMLDivElement>(null);
  const [previewMoreEntryEl, setPreviewMoreEntryEl] = useState<HTMLDivElement | null>(null);

  const closePreviewPopovers = useMemoizedFn(() => {
    setOpen(false);
    cardRootRef.current?.classList?.remove('dbl-show-card-icon');
  });

  const handlePreviewDropdownOpenChange = useMemoizedFn((nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(true);
      cardRootRef.current?.classList.add('dbl-show-card-icon');
      return;
    }
    setOpen(false);
    cardRootRef.current?.classList.remove('dbl-show-card-icon');
  });

  useClosePopoversWhenAnchorHidden(
    previewMoreEntryEl,
    !isEdit && open,
    closePreviewPopovers,
  );

  const overlayProps = getCardOverlayProps(false, cardRootRef.current);
  // const [forceVisible, setForceVisible] = useState(!!onForceClose);
  // 经分承接页gameCode
  const {gamecode}: any = useParams();
  const {formatMessage} = useIntl();

  const buildChartParams = useMemoizedFn((queries?: Record<string, unknown>): IDashboardChartRequest => ({
    game_code: gamecode,
    task_id: [String(taskId)],
    queries: {
      filters: Object.values(filterValues || []),
      user_filter: userFilter,
      ...queries,
    },
    system,
    is_sql_preview: false,
  }));

  const {
    previewSQL,
    loading: previewSqlLoading,
    error: previewSqlError,
    modalVisible: previewSqlModalVisible,
    hideModal: hidePreviewSqlModal,
    sqlContent: previewSqlContent,
  } = usePreviewSQL({
    fetchPreviewSql: () =>
      previewDashboardChartSql(ChartType.DimNumberTrend, buildChartParams()),
  });

  const requestData = useMemoizedFn((queries?: Record<string, unknown>) => {
    return calculateDashboardChart(ChartType.DimNumberTrend, buildChartParams(queries));
  });
  
  const {data, loading, error, run: getData}: any = useRequest(requestData, {
    manual: true,
    onError: (e) => {
      console.log('获取数字趋势卡片信息错误: ', e);
    }
  });
  
  useEffect(() => {
    !chartsInfoLoading && (filterValues || userFilter?.controls?.length) && getData();
    
  }, [filterValues, chartsInfoLoading, userFilter]);
  
  const {lineData, rankData, metricData, curMetricsName, metricKey, scopeFilters, userFilterInfo, granularity, isDataError, isSingleState, isHourDate, trendStatus, metricInfo } = useMemo(() => {
    
    try {
  
      // 1.
      if (!data) return {};
      const {chart_data, trend_metric = {}, trend: lineData,  dimension: rankData, last_date_dimension: lastDateRankData, column_name: columnName, metrics, user_filter} = data?.[taskId] || {};
      let metricInfo = columnName?.find(item => item.name === metrics[0]);
      const { accuracy, unit } = getOldChartDisplayConfig({...metricInfo, column_type: metricInfo.column_date_type}, ChartType.Table);
      metricInfo = {...metricInfo, accuracy, unit};
      
      
      // 2. 指标名称
      const curMetricsName = data?.[taskId]?.metrics?.[0]?.replace(/(t[0-2]_)/, '') || '';
  
      // 3. 获取scope的筛选器信息
      const scope = getScopeFilters(data?.[taskId]?.filters || []);
      
      /**
       * 4. 计算 日期粒度和范围
       * */
      // 图表中筛选区域的配置
      const chartFilters = JSON.parse(dataMsg?.filter || '{}');
      const chartXColumn = chartFilters?.group_by?.[0]; // X轴配置的 日期字段
      
  
      // 从后端返回的 日期筛选器中, 找到与X轴配置的相同的日期字段
      const newFilterDate = scope?.find(item => chartXColumn?.table_id === item.table_id && item.column === chartXColumn?.column);
      const info = {value: newFilterDate?.value ?? ['7-day'], dateType: newFilterDate?.dateType ?? 'day'};
      const {granularity, isSingle: isSingleDate} = getTrendDateInfo(info);
      const isHourDate = newFilterDate?.dateType === 'hour';
      // 图表配置
      const { showStaticTime = false } = JSON.parse(dataMsg?.column_infos || '{}')?.trendStatus || {};
      const trendStatus = {
        showStaticTime,
      };
      /**
       * 5 针对报表配置中的 isDefaultLastDate判断逻辑
       *
       * 前提:
       *   在图表中 将 create_time日期字段 设置为 X轴 和 Filter, 且设置最近14天;
       *
       * 场景1(对于趋势卡片来说, 存在多个日期筛选器):
       *    在报表中设置了insert_time字段作用于趋势图, 且设置了Default Last Date;
       * 结论:
       *    那么 趋势图 依然按照图表的 create_time 的14天 展示区间值和变化率
       *    (指标值 取 接口中的value)
       *
       * 场景2:
       *   在报表中设置了create_time日期字段, 且设置了Default Last Date;
       * 结论:
       *    那么进入页面时 趋势图 展示单点值 和同环比.
       *   (指标值 取 接口中的 last_date_value)
       */
      
      let isDefaultLastDate = false;
      // 遍历报表筛选器, 找到与图表X轴相同的日期字段
      filterValues.forEach((filter) => {
        const isSameColumn = filter?.column === chartXColumn?.column && filter?.table_id === chartXColumn?.table_id;
        filter.scope_charts?.forEach((scopeChart) => {
          // 查找报表筛选器是否作用于当前趋势卡片
          if (String(scopeChart.task_id) === String(taskId)) {
            if (isSameColumn) { // 当是报表筛选器和图表的x轴是相同字段时
              isDefaultLastDate = !!scopeChart.isDefaultDate;
            } else {
              // 查找关联字段是否和 图表X轴的日期字段 相同
              const {column, table_id} = scopeChart?.apply_to_column?.[0] ?? {};
              if (table_id === chartXColumn?.table_id && column === chartXColumn?.column) {
                isDefaultLastDate = !!scopeChart.isDefaultDate;
              }
            }
          }
        });
      });
      
      // 6. 显示 单点日期 状态, 还是范围日期 状态
      // isFilterInit 为true, 且 isDefaultLastDate为 true 显示单点日期状态
      // isFilterInit 为 false, 且 isSingleDate 为 true时, 显示单点日期状态
      const isSingleState = (isFilterInit && isDefaultLastDate && !isSingleDate) || isSingleDate;
      // 同环比或变化率
      const changData: {dod?: number; sdlw?: number; change?: number;} = {};
      switch (granularity) {
        case 'day': {
          changData.dod = trend_metric?.dod;
          changData.sdlw = trend_metric?.sdlw;
          break;
        }
        case 'week': {
          changData.dod = trend_metric?.wow;
          changData.sdlw = trend_metric?.swlm;
          break;
        }
        case 'month': {
          changData.dod = trend_metric?.mom;
          changData.sdlw = trend_metric?.smly;
          break;
        }
      }

      changData.change = trend_metric?.change;
      const newRankData = isSingleState ? lastDateRankData : rankData;
      return {
        lineData: hasEmptyProperty(lineData?.[0], ['series', 'xAxis']) ? [] : lineData,
        rankData: hasEmptyProperty(newRankData?.[0], ['series']) ? [] : newRankData,
        metricData: {
          trendChartData: chart_data?.[0]?.[0]?.metrics || [],
          dod: {...changData?.dod, extendFormatRule: {type: metricInfo?.unit || 'bmk', decimal: metricInfo?.accuracy || 0}},
          sdlw: {...changData?.sdlw, extendFormatRule: {type: metricInfo?.unit || 'bmk', decimal: metricInfo?.accuracy || 0}},
          change: {...changData?.change, extendFormatRule: {type: metricInfo?.unit || 'bmk', decimal: metricInfo?.accuracy || 0}},
          metricValue: formatDisplayValue({
            value: isSingleState ? trend_metric?.last_date_value : trend_metric?.value,
            isPercent: metricInfo?.column_date_type === 'percent',
            accuracy: metricInfo?.accuracy || 0,
            unit: metricInfo?.unit || 'bmk',
          }),
          staticTime: (isSingleState ? trend_metric?.last_date : trend_metric?.date) || '-',
        },
        metricKey: data?.[taskId]?.metrics?.[0],
        // 产品修改需求: 不需要显示聚合名称
        // curMetricsName: curMetricsName + `(${MetricTypes[trend_metric?.aggregation]?.[isEn ? 'text_en' : 'text']})`,
        curMetricsName,
        lastDate: (isFilterInit && isDefaultLastDate) ? `(${trend_metric.last_date})` : undefined,
        scopeFilters: scope,
        granularity,
        isSingleState,
        isHourDate,
        trendStatus,
        metricInfo,
        userFilterInfo: user_filter,
      };
      
    } catch (e) {
      
      console.log("报表趋势图卡片数据转换错误: ", e);
      return { isDataError: true};
    }

  }, [data]);
  
  const jumpToEdit = () => {
    const pathId = dataMsg.type === 1 ? 'Action/ExploreAnalysis' : 'Analysis/MultiAnalysis';
    const path = `/${SYSTEM_NAME}/${gamecode}/${pathId}?from=edit&taskId=${dataMsg.task_id}&name=${dataMsg?.name}&nameCn=${dataMsg?.name_cn}`;
    handleOnSubmit?.(path);
  };
  const failedMsg = useChartCardError(error);
  const isNoData = isEmpty(lineData) && isEmpty(rankData) && isEmpty(data?.[taskId]?.trend_metric);
  return (
    <>
    <div ref={cardRootRef} className="dbl-metric-card-root">
      <Card className={'dbl-dashboard-dimension-number-trend-card dbl-metric-card'}>
      <div className={classNames('dbl-card-icon-operation-box', 'dbl-metric-card-operation', !isEdit && 'dbl-card-preview-toolbar')}>
        {
          !isEdit && (!loading && !error) ? (
            <Dropdown
              trigger="hover"
              clickClose={false}
              boxClassName={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-more-menu')}
              button={
                <div ref={setPreviewMoreEntryEl} className={classNames('dbl-card-drag-operation', 'dbl-card-preview-more-entry', {action: open})}>
                  <SvgIcon type="there-points" />
                </div>
              }
              placementOffset={0}
              open={open}
              appearance="pure"
              placement="bottom-end"
              onOpenChange={handlePreviewDropdownOpenChange}
              {...overlayProps}
            >
              <List type="option">
                <List.Item className="dbl-card-preview-filter-menu-item">
                  <ShowFilterContentIcon
                    contentMap={{
                      [FILTER_CONTENT_KEY.GENERAL_FILTER]: { filters: scopeFilters},
                      [FILTER_CONTENT_KEY.USER_FILTER]: userFilterInfo,
                    }}
                    isEdit={true}
                    overlayClassName="dbl-card-preview-more-filter-overlay"
                    triggerEndContent={
                      <span className="dbl-card-preview-more-menu-label">
                        {formatMessage({id: 'dbl_filter_tab_current_filter'})}
                      </span>
                    }
                    onVisibleChange={(visible: boolean) => {
                      if (visible) {
                        cardRootRef.current?.classList.add('dbl-show-card-icon');
                      } else if (!open) {
                        cardRootRef.current?.classList.remove('dbl-show-card-icon');
                      }
                    }}
                    {...overlayProps}
                  />
                </List.Item>
                {
                  chartExplore && (dataMsg.type === 0) ? (
                    <List.Item
                      className="dbl-card-preview-explore-menu-item"
                      onClick={() => {
                        handleExportClick({
                          filters: data?.[taskId]?.filters,
                          system,
                          gamecode,
                          dataMsg
                        });
                        setOpen(false);
                      }}
                    >
                      <SvgIcon type="explore" />
                      {formatMessage({ id: 'dbl_create_dashboard_icon_explore' })}
                    </List.Item>
                  ) : null
                }
                <List.Item
                  onClick={() => {
                    previewSQL();
                    setOpen(false);
                  }}
                >
                  <SvgIcon type="open-eyes"/>
                  {formatMessage({ id: 'dbl_multi_preview_sql' })}
                </List.Item>
              </List>
            </Dropdown>
          ) : null
        }
        {
          isEdit && <Dropdown
            trigger="hover"
            clickClose={false}
            boxClassName={'dbl-card-drag-operation-dropdown'}
            button={
              <div className={classNames('dbl-card-drag-operation', {action: open})}>
                <SvgIcon type="there-points" />
              </div>
            }
            placementOffset={0}
            open={open}
            appearance="pure"
            placement="bottom-end"
            onOpenChange={(value) => {
              setOpen(value);
            }}
          >
            <List type="option">
              {
                (hasChartPrivilege && dataMsg?.edit) ? (<List.Item
                  onClick={() => {
                    jumpToEdit();
                    setOpen(false);
                  }}
                >
                  <SvgIcon type="pencil-m" />{formatMessage({ id: 'dbl_common_edit' })}
                </List.Item>
                ) : (
                  ''
                )}
              <List.Item
                onClick={() => {
                  previewSQL();
                  setOpen(false);
                }}
              >
                <SvgIcon type="open-eyes"/>
                {formatMessage({ id: 'dbl_multi_preview_sql' })}
              </List.Item>
              <List.Item
                onClick={() => {
                  setOpen(false);
                  deleteCard(dataMsg);
                }}
              >
                <SvgIcon type="trash-can-icon" />{formatMessage({ id: 'dbl_common_delete' })}
              </List.Item>
            </List>
          </Dropdown>
        }
      </div>
      <div className="dbl-metric-card-body">
        <Abnormal
          style={{
            width: '100%',
            height: '100%'
          }}
          isLoading={chartsInfoLoading || loading}
          isError={error || isDataError}
          nodataMsg={formatMessage({ id: "dbl_common_no_filter_data" })}
          failedMsg={failedMsg}
          isNoData={isNoData}
          component={(<Card.Body
            className="dbl-metric-card-body-inner"
            title={<>
              <SvgIcon type={'plate'} />
              {curMetricsName}
              {/* <span className={'dbl-dashboard-trend-time'}>{lastDate}</span> */}
            </>}
            operation={<div className='dbl-metric-card-placeholder' />}
          >
            <DimNumberTrendCard
              className={'dashboard'}
              height={75}
              metricData={metricData}
              lineData={lineData}
              rankData={rankData}
              metricKey={metricKey}
              metricName={curMetricsName}
              granularity={granularity}
              isSingle={isSingleState}
              isShowTrend={!isHourDate}
              isShowDod={!isHourDate}
              isShowStaticTime={trendStatus?.showStaticTime}
              metricInfo={metricInfo}
            />
          </Card.Body>)}
        />
      </div>
    </Card>
    </div>
    <PreviewSQLModal
      visible={previewSqlModalVisible}
      onClose={hidePreviewSqlModal}
      sql={previewSqlContent}
      loading={previewSqlLoading}
      error={previewSqlError}
    />
    </>
  );
};

export default DimNumberTrendTragCard;
